import { Link } from "react-router-dom";
import { useState } from "react";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useAuth } from "@/state/AuthContext";

export function OrgSwitcher({ className }: { className?: string }) {
  const { tenant, memberships, switchTenant, user } = useAuth();
  if (user?.supportMode || !tenant || memberships.length <= 1) return null;

  return (
    <Select
      value={tenant.id}
      onChange={async (e) => {
        await switchTenant(e.target.value);
        window.location.reload();
      }}
      className={cn("max-w-40 text-xs h-8", className)}
    >
      {memberships.map((m) => (
        <option key={m.tenantId} value={m.tenantId}>
          {m.tenantName}
        </option>
      ))}
    </Select>
  );
}

export function SubscriptionBanner() {
  const { tenant, user } = useAuth();
  if (!tenant) return null;

  if (user?.supportMode) {
    return <SupportBanner />;
  }

  const status = tenant.subscriptionStatus;
  if (status === "active") return null;

  let message = "";
  let className = "bg-amber-400 text-black";

  if (status === "trialing" && tenant.trialEndsAt) {
    const days = Math.ceil(
      (new Date(tenant.trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
    );
    message = `Starter trial — ${Math.max(0, days)} day(s) remaining.`;
  } else if (status === "grace") {
    message = "Trial ended — read-only mode. Subscribe to keep editing.";
    className = "bg-orange-500 text-white";
  } else if (status === "locked") {
    message = "Subscription required to access InFloTrack.";
    className = "bg-red-600 text-white";
  } else if (status === "suspended") {
    message = "This organization has been suspended.";
    className = "bg-red-600 text-white";
  }

  if (!message) return null;

  return (
    <div className={`text-center text-xs font-semibold py-1.5 px-4 ${className}`}>
      {message}{" "}
      {(status === "trialing" || status === "grace" || status === "locked") && (
        <Link to="/admin/billing" className="underline ml-1">
          Billing
        </Link>
      )}
    </div>
  );
}

export function SupportBanner() {
  const { tenant, exitSupport } = useAuth();
  const [exiting, setExiting] = useState(false);

  if (!tenant) return null;

  const onExit = async () => {
    setExiting(true);
    const result = await exitSupport();
    setExiting(false);
    if (result.success) {
      window.location.assign("/admin/platform");
    }
  };

  return (
    <div className="bg-violet-600 text-white text-center text-xs font-semibold py-1.5 px-4">
      Support session — viewing <span className="underline">{tenant.name}</span>
      <button
        type="button"
        onClick={() => void onExit()}
        disabled={exiting}
        className="ml-3 underline hover:text-white/90 disabled:opacity-60"
      >
        {exiting ? "Exiting…" : "Exit support"}
      </button>
    </div>
  );
}
